<template>
  <ModalConfirmation
    :showClose="false"
    :isSubmitButtonLoading="isSubmitting"
    :customWidth="'650px'"
    :noMarginOnButton="true"
    :appendToBody="appendToBody"
    @close="$emit('close')"
    @confirm="onConfirmDelete"
    :isShowConfirmDeleteMessage="false">
    <div slot="custom_warning">
      <div class="away_warning">
        <div v-if="uncancelGroups.length > 0" class="away_warning__lessons">
          <strong>{{ i18n.lessons_will_be_restored }}</strong>
          <div v-for="grp in uncancelGroups" :key="'u_'+grp.name">
            <table class="away_warning__lessons__table">
              <tbody>
              <tr v-for="ev in grp.events" :key="ev.event_id + '_' + grp.name" class="marg-0-5-top">
                <td>
                  {{ grp.name }} | {{ ev.title }}
                  <span class="text-gray">({{ humanTime(ev.start, ev.end) }})</span>
                </td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="away_warning__text">
          <h4 class="marg-0-bottom">{{ i18n.confirm_delete_title }}</h4>
          <p class="pad-1 italic">{{ i18n.confirm_delete_text }}</p>
          <p class="pad-1 italic pad-0-top" v-if="uncancelGroups.length === 0">
            {{ i18n.no_lessons_to_restore }}
          </p>
        </div>
      </div>
    </div>

    <p slot="confirmText">{{ i18n.delete }}</p>
  </ModalConfirmation>
</template>

<script>
import ModalConfirmation from '../Modals/ModalConfirmation.vue'
import docendo from '../../docendo'
import moment from 'moment'

const I18N = {
  da: {
    delete: 'Slet',
    confirm_delete_title: 'Er du sikker på, at du vil slette denne lektion?',
    confirm_delete_text: 'Lektionen slettes. Aflyste lektioner i perioden vil blive gendannet.',
    lessons_will_be_restored: 'Følgende lektioner vil blive gendannet',
    no_lessons_to_restore: 'Der er ingen lektioner at gendanne i perioden'
  },
  nb: {
    delete: 'Slett',
    confirm_delete_title: 'Er du sikker på at du vil slette denne leksjonen?',
    confirm_delete_text: 'Leksjonen slettes. Avlyste leksjoner i perioden vil bli gjenopprettet.',
    lessons_will_be_restored: 'Følgende leksjoner vil bli gjenopprettet',
    no_lessons_to_restore: 'Det er ingen leksjoner å gjenopprette i perioden'
  },
  en: {
    delete: 'Delete',
    confirm_delete_title: 'Are you sure you want to delete this lesson?',
    confirm_delete_text: 'The lesson will be deleted. Canceled lessons in the period will be restored.',
    lessons_will_be_restored: 'The following lessons will be restored',
    no_lessons_to_restore: 'There are no lessons to restore in the period'
  }
}

export default {
  components: {ModalConfirmation},
  props: {
    event: {
      type: Object,
      default: () => ({})
    },
    eventIdProp: {
      default: 'event_id'
    },
    appendToBody: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      isSubmitting: false,
      klassEvents: []
    }
  },
  computed: {
    locale () {
      return this.$store.getters.locale
    },
    i18n () {
      const locale = this.locale === 'nn' || this.locale === 'no' ? 'nb' : this.locale
      return I18N[locale] || I18N.en
    },
    selfEventId () {
      if (!this.event) return null
      if (this.event[this.eventIdProp] != null) return this.event[this.eventIdProp]
      return this.event.event_id != null ? this.event.event_id : this.event.id
    },
    eventCalendars () {
      if (!this.event) return []
      if (Array.isArray(this.event.calendars) && this.event.calendars.length > 0) {
        return this.event.calendars
      }
      if (Array.isArray(this.event.calendar_events)) {
        return this.event.calendar_events
      }
      return []
    },
    uncancelCandidates () {
      if (!Array.isArray(this.klassEvents)) return []
      var selfId = this.selfEventId
      return this.klassEvents.filter(ev => {
        const isCanceled = ev && ev.canceled === true
        const notAway = ev && ev.away !== true
        const notSelf = !selfId || (ev.event_id !== selfId && ev.id !== selfId)
        return isCanceled && notAway && notSelf
      })
    },
    uncancelGroups () {
      return this.groupByClass(this.uncancelCandidates)
    }
  },
  methods: {
    groupByClass (events) {
      if (!Array.isArray(events)) return []
      const bucket = {}
      events.forEach(function (ev) {
        const calendars = (ev.calendars || []).filter(function (c) {
          return c && c.calendar_type === 'klass'
        })
        if (calendars.length === 0) {
          const key = 'Uden klasse'
          if (!bucket[key]) bucket[key] = []
          bucket[key].push(ev)
        } else {
          calendars.forEach(function (c) {
            const key = c && c.name ? c.name : 'Uden klasse'
            if (!bucket[key]) bucket[key] = []
            bucket[key].push(ev)
          })
        }
      })
      return Object.keys(bucket)
        .sort(function (a, b) {
          return a.localeCompare(b)
        })
        .map(function (name) {
          const list = bucket[name].slice().sort(function (a, b) {
            return moment(a.start).valueOf() - moment(b.start).valueOf()
          })
          return {name: name, events: list}
        })
    },
    humanTime (startIso, endIso) {
      const s = moment(startIso)
      const e = moment(endIso)
      return s.format('LT') + ' - ' + e.format('LT')
    },
    onConfirmDelete () {
      this.isSubmitting = true
      this.restoreCanceledEvents(() => {
        this.deleteEvent(success => {
          this.isSubmitting = false
          this.$emit('deleted', success)
          this.$emit('close')
        })
      })
    },
    restoreCanceledEvents (callback) {
      if (this.uncancelCandidates.length === 0) {
        callback()
        return
      }
      const schoolId = this.event.school_id
      const updates = this.uncancelCandidates.map(function (ev) {
        return {event_id: ev.event_id || ev.id, canceled: false}
      })
      docendo.updateEvents(
        schoolId,
        updates,
        () => {
          callback()
        },
        () => {
          console.log('failed to restore canceled events')
          callback()
        }
      )
    },
    deleteEvent (callback) {
      const schoolId = this.event.school_id
      const eventId = this.selfEventId
      docendo.deleteEvent(
        schoolId,
        eventId,
        success => {
          callback(success)
        },
        failure => {
          this.isSubmitting = false
          console.log('failed to delete event', failure)
        }
      )
    },
    readKlassEvents () {
      const dbEventStart = moment(this.event.start).format('YYYY-MM-DD HH:mm')
      const dbEventEnd = moment(this.event.end).format('YYYY-MM-DD HH:mm')

      const calendarIds = this.eventCalendars
        .filter(function (calendar) {
          return calendar.calendar_type === 'klass'
        })
        .map(function (calendar) {
          return calendar.calendar_id
        })

      if (calendarIds.length === 0) {
        this.klassEvents = []
        return
      }

      docendo.calendarEvents2(
        calendarIds,
        dbEventStart,
        dbEventEnd,
        null,
        success => {
          this.klassEvents = success && success.length > 0 ? JSON.parse(success) : []
        },
        failure => {
          console.log('Error fetching class events', failure)
        }
      )
    }
  },
  mounted () {
    this.readKlassEvents()
  }
}
</script>

<style scoped>
.away_warning {
  display: flex;
  flex-direction: row-reverse;
  align-items: stretch;
  max-height: calc(100vh - 50px);
  overflow-y: auto;
}

.away_warning > div {
  flex: 1;
}

.away_warning__lessons {
  padding: 2rem 1rem;
  background: #edf0f5;
  min-width: 400px;
}

.away_warning__lessons__table {
  width: 100%;
  border-width: 1px 1px 0 1px;
  border-collapse: collapse;
  background: #f6f9fc;
  margin-top: 0.5rem;
  border-radius: 0.25rem;
}

.away_warning__lessons__table th,
.away_warning__lessons__table td {
  padding: 0.3rem 0.5rem;
  text-align: left;
}
</style>



// WEBPACK FOOTER //
// DModalDeleteAwayEvent.vue?e4f121e0